import { CURRENT_AWARD_YEAR } from '../constants/evaluation';
import {
  CATEGORY_INNOVATION,
  CATEGORY_SUSTAINABLE,
  CATEGORY_DIGITAL_TRANSFORMATION,
  CATEGORY_AI,
  CATEGORY_YOUNG_LEADER,
  CATEGORY_WOMEN_IN_TECH,
  CATEGORY_NEW_BUSINESS_VENTURE,
  CATEGORY_WOMEN_IN_TECH_2026,
  CATEGORY_DIGITAL_CATALYST,
  CATEGORIES_BY_YEAR,
} from '../constants/categoryNames';

const COMMON_MAPPINGS = {
  submission_type: 'submission_type',
  pillar: 'pillar',
  organization_unit: 'org_unit',
  full_name: 'full_name',
  email: 'email',
  phone: 'phone',
  additional_comments: 'additional_comments',
  title: 'title',
};

const PILLAR_OPTIONS = [
  { value: 'Agri-business & Food', label: 'Sinar Mas Agribusiness and Food' },
  { value: 'Pulp & Paper', label: 'APP Group' },
  { value: 'Developer & Real Estate', label: 'Sinar Mas Land' },
  {
    value: 'Financial Services',
    label: 'Sinar Mas Financial Services & Mining',
  },
  {
    value: 'Energy & Infrastructure',
    label: 'Sinar Mas Energy and Infrastructure',
  },
  {
    value: 'Communications & Technology',
    label: 'Sinar Mas Communication & Technology',
  },
  { value: 'Healthcare', label: 'Sinar Mas Healthcare' },
  { value: 'Others', label: 'Sinar Mas Education' },
];

const COMMON_FIELDS = [
  {
    name: 'pillar',
    type: 'select',
    label: 'Pillar',
    required: true,
    defaultValue: 'Agri-business & Food',
    options: PILLAR_OPTIONS,
  },
  {
    name: 'full_name',
    type: 'text',
    label: 'Full Name / Project Lead',
    required: true,
    placeholder: 'Enter Full Name or Project Lead name',
  },
  {
    name: 'phone',
    type: 'text',
    label: 'Contact Number (WA)',
    required: true,
    placeholder: 'e.g. 08123456789',
  },
  {
    name: 'email',
    type: 'text',
    label: 'Email',
    required: true,
    placeholder: 'e.g. user@company.com',
  },
  {
    name: 'organization_unit',
    type: 'text',
    label: 'Business Unit (Company Name)',
    required: true,
    placeholder: 'Enter Business Unit or Company Name',
  },
];

const TITLE_FIELD = {
  name: 'title',
  type: 'hidden',
  label: 'Title',
  required: false,
  defaultValue: '-',
};

const ADDITIONAL_COMMENTS = {
  name: 'additional_comments',
  type: 'textarea',
  label: 'Additional Comments (Optional)',
  required: false,
  placeholder:
    "Any additional information you'd like to share about your innovation...",
};

const SUBMISSION_ORG = {
  name: 'submission_type',
  type: 'text',
  label: 'Submission Type',
  required: true,
  defaultValue: 'organization',
  displayValue: 'Organization',
  readonly: true,
};

const SUBMISSION_INDIVIDU = {
  name: 'submission_type',
  type: 'text',
  label: 'Submission Type',
  required: true,
  defaultValue: 'individual',
  displayValue: 'Individual',
  readonly: true,
};

const PROJECT_NAME = {
  name: 'project_name',
  type: 'text',
  label: 'Project Name',
  required: true,
  placeholder: 'Provide a descriptive name for your project or initiative...',
};

const IMPACT = {
  name: 'impact',
  type: 'textarea',
  label: 'Impact',
  required: true,
  placeholder:
    'Explain the measurable impact of your innovation, including benefits to the organization, customers, or society...',
};

const ACHIEVED_RESULT = {
  name: 'achieved_result',
  type: 'textarea',
  label: 'Achieved Result',
  required: true,
  placeholder:
    'Detail the specific outcomes and results achieved through your innovation...',
};

const EXAMPLE_OF_INNOVATION = {
  name: 'example_of_innovation',
  type: 'textarea',
  label: 'Example of Innovation',
  required: true,
  placeholder:
    'Describe the innovative aspects of your project, including new technologies, processes, or approaches used...',
};

const SCALABILITY_SUCCESS = {
  name: 'scalability_success',
  type: 'textarea',
  label: 'Scalability and Implementation Success',
  required: true,
  placeholder:
    'Explain how your solution can be scaled and detail the implementation success metrics and outcomes...',
};

const LEADERSHIP_ACHIEVEMENT = {
  name: 'leadership_achievement',
  type: 'text',
  label: 'Leadership Achievement',
  required: true,
  placeholder:
    'Describe your leadership achievements, including teams led, initiatives driven, and impact...',
};

const IMPACT_IN_ORG = {
  name: 'impact_in_org',
  type: 'textarea',
  label: 'Impact in Organization',
  required: true,
  placeholder:
    'Explain the measurable impact of your contributions within your organization, including specific metrics and outcomes...',
};

const INNOVATIVE_CONTRIBUTION = {
  name: 'innovative_contribution',
  type: 'textarea',
  label: 'Innovative Contribution',
  required: true,
  placeholder:
    'Describe the innovative aspects of your project, including new technologies, processes, or approaches used...',
};

const ADVOCACY_MENTORSHIP = {
  name: 'advocacy_mentorship',
  type: 'textarea',
  label: 'Advocacy and Mentorship',
  required: true,
  placeholder:
    'Describe your advocacy efforts and mentorship activities, including programs led and impact on others...',
};

const BASE_SCHEMA_CONFIG = {
  templateName: 'smdd_awards_form',
  description: 'SMDD - Awards Submission Form',
  fieldMappings: {
    [CATEGORY_INNOVATION]: {
      ...COMMON_MAPPINGS,
      project_name: 'description',
      example_of_innovation: 'technology_used',
      impact: 'innovation_description',
      achieved_result: 'impact_and_results',
    },
    [CATEGORY_SUSTAINABLE]: {
      ...COMMON_MAPPINGS,
      project_name: 'description',
      environmental_impact: 'environmental_impact',
      innovation_technology: 'innovation_description',
      scalability_success: 'scalability_potential',
    },
    [CATEGORY_DIGITAL_TRANSFORMATION]: {
      ...COMMON_MAPPINGS,
      project_name: 'description',
      initiative: 'technology_used',
      impact: 'innovation_description',
      achieved_result: 'impact_and_results',
    },
    [CATEGORY_AI]: {
      ...COMMON_MAPPINGS,
      ai_project_name: 'description',
      ai_solution: 'technology_used',
      impact: 'impact_and_results',
      scalability_success: 'scalability_potential',
    },
    [CATEGORY_YOUNG_LEADER]: {
      ...COMMON_MAPPINGS,
      leadership_achievement: 'description',
      innovative_contribution: 'innovation_description',
      impact_in_org: 'impact_and_results',
    },
    [CATEGORY_WOMEN_IN_TECH]: {
      ...COMMON_MAPPINGS,
      leadership_achievement: 'description',
      advocacy_mentorship: 'advocacy_and_mentorship',
      impact_in_org: 'impact_and_results',
    },
    [CATEGORY_NEW_BUSINESS_VENTURE]: {
      ...COMMON_MAPPINGS,
      project_name: 'description',
      example_of_innovation: 'technology_used',
      impact: 'innovation_description',
      achieved_result: 'impact_and_results',
    },
    [CATEGORY_DIGITAL_CATALYST]: {
      ...COMMON_MAPPINGS,
      leadership_achievement: 'description',
      innovative_contribution: 'innovation_description',
      impact_in_org: 'impact_and_results',
    },
    [CATEGORY_WOMEN_IN_TECH_2026]: {
      ...COMMON_MAPPINGS,
      leadership_achievement: 'description',
      advocacy_mentorship: 'advocacy_and_mentorship',
      impact_in_org: 'impact_and_results',
    },
  },
  fieldsByCategory: {
    [CATEGORY_INNOVATION]: [
      SUBMISSION_ORG,
      ...COMMON_FIELDS,
      PROJECT_NAME,
      EXAMPLE_OF_INNOVATION,
      IMPACT,
      ACHIEVED_RESULT,
      ADDITIONAL_COMMENTS,
      TITLE_FIELD,
    ],
    [CATEGORY_SUSTAINABLE]: [
      SUBMISSION_ORG,
      ...COMMON_FIELDS,
      PROJECT_NAME,
      {
        name: 'environmental_impact',
        type: 'textarea',
        label: 'Environmental Impact and Sustainability',
        required: true,
        placeholder:
          'Describe the environmental benefits and sustainability aspects of your solution, including measurable outcomes...',
      },
      {
        name: 'innovation_technology',
        type: 'textarea',
        label: 'Innovation & Technology Used',
        required: true,
        placeholder:
          'Describe the innovative aspects of your project, including new technologies, processes, or approaches used...',
      },
      SCALABILITY_SUCCESS,
      ADDITIONAL_COMMENTS,
      TITLE_FIELD,
    ],
    [CATEGORY_DIGITAL_TRANSFORMATION]: [
      SUBMISSION_ORG,
      ...COMMON_FIELDS,
      PROJECT_NAME,
      {
        name: 'initiative',
        type: 'textarea',
        label: 'Initiative',
        required: true,
        placeholder:
          'Describe your digital transformation initiative, including objectives, scope, and key activities...',
      },
      IMPACT,
      ACHIEVED_RESULT,
      ADDITIONAL_COMMENTS,
      TITLE_FIELD,
    ],
    [CATEGORY_AI]: [
      SUBMISSION_ORG,
      ...COMMON_FIELDS,
      {
        name: 'ai_project_name',
        type: 'text',
        label: 'AI Project Name',
        required: true,
        placeholder:
          'Provide a descriptive name for your AI project or solution...',
      },
      {
        name: 'ai_solution',
        type: 'textarea',
        label: 'AI Solution',
        required: true,
        placeholder:
          'Describe your AI solution, including the technology stack, algorithms, and implementation approach...',
      },
      IMPACT,
      SCALABILITY_SUCCESS,
      ADDITIONAL_COMMENTS,
      TITLE_FIELD,
    ],
    [CATEGORY_YOUNG_LEADER]: [
      SUBMISSION_INDIVIDU,
      ...COMMON_FIELDS,
      LEADERSHIP_ACHIEVEMENT,
      INNOVATIVE_CONTRIBUTION,
      IMPACT_IN_ORG,
      ADDITIONAL_COMMENTS,
      TITLE_FIELD,
    ],
    [CATEGORY_WOMEN_IN_TECH]: [
      SUBMISSION_INDIVIDU,
      ...COMMON_FIELDS,
      LEADERSHIP_ACHIEVEMENT,
      ADVOCACY_MENTORSHIP,
      IMPACT_IN_ORG,
      ADDITIONAL_COMMENTS,
      TITLE_FIELD,
    ],
    [CATEGORY_NEW_BUSINESS_VENTURE]: [
      SUBMISSION_ORG,
      ...COMMON_FIELDS,
      PROJECT_NAME,
      EXAMPLE_OF_INNOVATION,
      IMPACT,
      ACHIEVED_RESULT,
      ADDITIONAL_COMMENTS,
      TITLE_FIELD,
    ],
    [CATEGORY_DIGITAL_CATALYST]: [
      SUBMISSION_INDIVIDU,
      ...COMMON_FIELDS,
      LEADERSHIP_ACHIEVEMENT,
      INNOVATIVE_CONTRIBUTION,
      IMPACT_IN_ORG,
      ADDITIONAL_COMMENTS,
      TITLE_FIELD,
    ],
    [CATEGORY_WOMEN_IN_TECH_2026]: [
      SUBMISSION_INDIVIDU,
      ...COMMON_FIELDS,
      LEADERSHIP_ACHIEVEMENT,
      ADVOCACY_MENTORSHIP,
      IMPACT_IN_ORG,
      ADDITIONAL_COMMENTS,
      TITLE_FIELD,
    ],
  },
};

export const getSmddFormSchema = (year: number = CURRENT_AWARD_YEAR) => {
  const categories =
    CATEGORIES_BY_YEAR[year] ?? CATEGORIES_BY_YEAR[CURRENT_AWARD_YEAR] ?? [];

  return {
    ...BASE_SCHEMA_CONFIG,
    templateName: `smdd_awards_form_${year}`,
    description: `SMDD - Awards Submission Form ${year}`,
    categories,
  };
};

export const smddFormSchema = getSmddFormSchema(CURRENT_AWARD_YEAR);
export default smddFormSchema;
